/**
 * Free Start tab — the funnel for members who begin without a card.
 *
 * Deliberately NOT a copy of the Trial tab. A trialist converts by not
 * cancelling; a free-month member has given us nothing and has to be earned.
 * So the hero of this tab is not the funnel — it's the at-risk list, which is
 * who to reach out to while their window is still open.
 *
 * There is no date-range control: every member runs their own 31-day clock
 * from their own redemption date, so a calendar window would slice people
 * mid-clock and make each stage mean something different per person.
 */
import React from 'react';
import { AlertTriangle, Users } from 'lucide-react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';

interface Stage {
  key: string;
  label: string;
  count: number;
  pct_of_redeemed: number;
}

interface AtRiskMember {
  user_id: string;
  days_left: number;
  last_active: string | null;
  has_goal: boolean;
  has_workout: boolean;
  /** Present only when a super_admin requested reveal. */
  email?: string;
  first_name?: string;
}

interface FreeStartPayload {
  definition: string;
  first_redemption: string | null;
  stages: Stage[];
  flow_rates: Record<string, number>;
  now: { in_free_window: number; churned: number; converted: number };
  engagement: { avg_active_days: number; avg_messages: number; avg_workouts: number };
  at_risk: AtRiskMember[];
  at_risk_quiet_days: number;
  revealed: boolean;
}

interface Props {
  revealed?: boolean;
  isSuperAdmin?: boolean;
}

const Tile: React.FC<{ label: string; value: React.ReactNode; hint?: string; tone?: string }> = ({
  label, value, hint, tone = 'text-gray-900',
}) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
    {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
  </div>
);

export const FreeStartTab: React.FC<Props> = ({ revealed = false, isSuperAdmin = false }) => {
  const qs = revealed && isSuperAdmin ? '?reveal=true' : '';
  const { data, isLoading, error } = useAnalyticsQuery<FreeStartPayload>(
    ['free-start', qs],
    `/api/admin/analytics/free-start/funnel${qs}`,
  );

  if (isLoading) return <div className="p-8 text-center text-gray-500">Loading free-start funnel…</div>;
  if (error) return <div className="p-8 text-center text-red-600">Couldn’t load the free-start funnel.</div>;

  const d = data?.data;
  if (!d) return null;

  const redeemed = d.stages[0]?.count ?? 0;

  // Nobody has redeemed yet — say so plainly rather than rendering a wall of zeros.
  if (redeemed === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
        <Users className="mx-auto mb-3 h-8 w-8 text-gray-400" />
        <div className="text-sm font-medium text-gray-900">No free-month members yet</div>
        <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
          This tab fills in as codes are redeemed. Every stage is anchored on each
          member’s own redemption date, so it stays meaningful no matter when they join.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="In their free month" value={d.now.in_free_window} hint="Clock still running" />
        <Tile
          label="Subscribed"
          value={d.now.converted}
          hint={`${d.stages.find((s: Stage) => s.key === 'converted')?.pct_of_redeemed ?? 0}% of members`}
          tone="text-emerald-700"
        />
        <Tile label="Ended without subscribing" value={d.now.churned} tone="text-amber-700" />
      </div>

      {/* At-risk first: it is the only section that is actionable today. */}
      <section className="rounded-lg border border-amber-200 bg-amber-50/50">
        <header className="flex items-center gap-2 border-b border-amber-200 px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-amber-900">
            Going quiet — reach out while the window is open
          </h3>
          <span className="ml-auto text-xs text-amber-800">
            no activity in {d.at_risk_quiet_days}+ days
          </span>
        </header>
        {d.at_risk.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-amber-800">
            Nobody is drifting right now — every active member has shown up in the last{' '}
            {d.at_risk_quiet_days} days.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-amber-800">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Member</th>
                  <th className="px-4 py-2 text-left font-medium">Days left</th>
                  <th className="px-4 py-2 text-left font-medium">Last active</th>
                  <th className="px-4 py-2 text-left font-medium">Got started?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-200/70">
                {d.at_risk.map((u: AtRiskMember) => (
                  <tr key={u.user_id}>
                    <td className="px-4 py-2 text-gray-900">
                      {u.email ?? `${u.user_id.slice(0, 8)}…`}
                      {u.first_name && <span className="ml-1 text-gray-500">({u.first_name})</span>}
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      <span className={u.days_left <= 7 ? 'font-semibold text-red-700' : 'text-gray-700'}>
                        {u.days_left}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {u.last_active ?? <span className="text-red-700">never</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {u.has_workout ? 'Training' : u.has_goal ? 'Goal only' : 'Not yet'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">Free-start funnel</h3>
        <div className="space-y-2">
          {d.stages.map((s: Stage) => (
            <div key={s.key} className="flex items-center gap-3">
              <div className="w-44 shrink-0 text-sm text-gray-700">{s.label}</div>
              <div className="h-6 flex-1 overflow-hidden rounded bg-gray-100">
                <div
                  className={`h-full rounded ${s.key === 'converted' ? 'bg-emerald-500' : 'bg-cedar-600'}`}
                  style={{ width: `${Math.max(s.pct_of_redeemed, 1)}%`, backgroundColor: s.key === 'converted' ? undefined : '#1F6C4F' }}
                />
              </div>
              <div className="w-24 shrink-0 text-right text-sm tabular-nums text-gray-900">
                {s.count} <span className="text-gray-500">({s.pct_of_redeemed}%)</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="Avg active days" value={d.engagement.avg_active_days} hint="Within their free window" />
        <Tile label="Avg messages" value={d.engagement.avg_messages} />
        <Tile label="Avg workouts" value={d.engagement.avg_workouts} />
      </div>

      <p className="rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-600">{d.definition}</p>
    </div>
  );
};

export default FreeStartTab;
